import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { authFetch } from "../api/orchestrator";
import { WhatsAppEntity } from "../types/entity";

type SalesReviewChat = {
  chatId: string;
  contact: string;
  chatType: "Direct" | "Lead Group" | "Internal";
  includedInReview: boolean;
  appointmentScore: number;
  rapportScore: number;
  sellingScore: number;
  verdict: string;
  summary: string;
  whatWentWell: string[];
  whatWentWrong: string[];
  improvements: string[];
  evidence: string[];
  lastActivity: string;
  metrics: {
    totalMessages: number;
    outboundMessages: number;
    inboundMessages: number;
    responseMinutes: number | null;
  };
};

type SalesReviewResponse = {
  ok: boolean;
  data: {
    entity: {
      id: string;
      repSlug: string;
      name: string;
      phone: string;
      timezone: string;
    };
    range: {
      from: string;
      to: string;
    };
    summary: {
      totalChats: number;
      includedChats: number;
      excludedChats: number;
      directChats: number;
      groupChats: number;
      avgAppointmentScore: number;
      avgRapportScore: number;
      avgSellingScore: number;
      chatsWithStrongAppointmentSignals: number;
      chatsWithNoOutboundMessages: number;
    };
    chats: SalesReviewChat[];
  };
  error?: string;
};

const scoreColor = (score: number) => {
  if (score >= 8) return "#15803d";
  if (score >= 5) return "#ca8a04";
  return "#b91c1c";
};

const scorePill = (score: number): React.CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 42,
  padding: "6px 10px",
  borderRadius: 999,
  color: "#fff",
  fontSize: 13,
  fontWeight: 700,
  background: scoreColor(score),
});

const cardStyle: React.CSSProperties = {
  background: "#fffdf8",
  border: "1px solid #ecdcc1",
  borderRadius: 18,
  padding: 20,
  boxShadow: "0 10px 24px rgba(20, 33, 61, 0.08)",
};

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString("en-SG", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const normalizeRepSlug = (entity: Partial<WhatsAppEntity>) =>
  (entity.rep_slug || entity.name || entity.phone || "")
    .toLowerCase()
    .replace(/^-+|-+$/, "");

const SalesReviewDashboard: React.FC = () => {
  const { rep = "prab" } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logout } = useAuth0();

  const from = searchParams.get("from") || "2026-06-01";
  const to = searchParams.get("to") || "2026-07-03";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [entities, setEntities] = useState<WhatsAppEntity[]>([]);
  const [data, setData] = useState<SalesReviewResponse["data"] | null>(null);
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);

  useEffect(() => {
    setDraftFrom(from);
    setDraftTo(to);
  }, [from, to]);

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        const response = await authFetch("/api/entities");
        const payload = await response.json();
        if (payload.ok) {
          setEntities(payload.entities || []);
        }
      } catch (err) {
        console.error("Failed to fetch entities", err);
      }
    };
    fetchEntities();
  }, []);

  useEffect(() => {
    const fetchReview = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await authFetch(
          `/api/analytics/sales-review/${encodeURIComponent(rep)}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
        );
        const payload: SalesReviewResponse = await response.json();
        if (!payload.ok) {
          throw new Error(payload.error || "Failed to load sales review");
        }
        setData(payload.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load sales review");
      } finally {
        setLoading(false);
      }
    };
    fetchReview();
  }, [rep, from, to]);

  const selectableEntities = useMemo(
    () =>
      entities
        .filter((entity) => entity.rep_slug)
        .map((entity) => ({
          ...entity,
          normalized_rep_slug: normalizeRepSlug(entity),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [entities]
  );

  const handleEntityChange = (nextSlug: string) => {
    navigate(`/analytics/sales-review/${nextSlug}?from=${draftFrom}&to=${draftTo}`);
  };

  const handleApplyRange = () => {
    navigate(`/analytics/sales-review/${rep}?from=${draftFrom}&to=${draftTo}`);
  };

  if (loading) {
    return <div style={{ padding: 24 }}>Loading sales review...</div>;
  }

  if (error || !data) {
    const isAuthError = error?.toLowerCase().includes("unauthorized") ||
                        error?.toLowerCase().includes("authentication token");
    return (
      <div style={{ padding: 24 }}>
        <h2>Sales Review</h2>
        <div style={{ color: "#b91c1c", background: "#fff1f2", padding: 16, borderRadius: 10 }}>
          {isAuthError
            ? "Your session has expired. Please log out and log back in."
            : (error || "No data available")}
        </div>
        {isAuthError && (
          <button
            style={{
              marginTop: 12, padding: "8px 20px", background: "#1d4ed8",
              color: "#fff", border: "none", borderRadius: 6, cursor: "pointer",
              fontSize: 14
            }}
            onClick={() => {
              const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
              const audience = import.meta.env.VITE_AUTH0_API_AUDIENCE;
              if (clientId && audience) {
                // Auth0 SPA JS v2 cache key format uses audience, not domain
                const cacheKey = `@@auth0spajs@@::${clientId}::${audience}::openid profile email offline_access`;
                localStorage.removeItem(cacheKey);
              }
              logout({ logoutParams: { returnTo: window.location.origin } });
            }}
          >
            Log Out &amp; Log In Again
          </button>
        )}
      </div>
    );
  }

  const includedChats = data.chats.filter((chat) => chat.includedInReview);
  const connectionEntity = selectableEntities.find(
    (entity) => entity.normalized_rep_slug === data.entity.repSlug
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #fff6e8 0%, #fefcf7 42%, #f4f7fb 100%)",
        padding: "24px 24px 56px",
      }}
    >
      <div style={{ maxWidth: 1320, margin: "0 auto" }}>
        <section
          style={{
            ...cardStyle,
            background: "linear-gradient(135deg, #14213d 0%, #274c77 55%, #6b8ead 100%)",
            color: "#fff",
            border: "none",
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 20, justifyContent: "space-between" }}>
            <div style={{ maxWidth: 760 }}>
              <div
                style={{
                  display: "inline-block",
                  padding: "6px 12px",
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.15)",
                  fontSize: 13,
                  fontWeight: 700,
                  marginBottom: 14,
                }}
              >
                Entity-Based Sales Review
              </div>
              <h1 style={{ margin: 0, fontSize: 34, lineHeight: 1.08 }}>
                {data.entity.name}
              </h1>
              <p style={{ margin: "12px 0 0", maxWidth: 720, lineHeight: 1.6, color: "rgba(255,255,255,0.88)" }}>
                This dashboard reviews all WhatsApp chats on the selected rep entity in the chosen date range.
                It does not depend on deprecated CRM owner fields.
              </p>
            </div>

            <div style={{ minWidth: 280, background: "rgba(255,255,255,0.14)", padding: 18, borderRadius: 18 }}>
              <div style={{ fontSize: 13, opacity: 0.85 }}>Rep entity</div>
              <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>{data.entity.phone}</div>
              <div style={{ fontSize: 13, opacity: 0.85, marginTop: 12 }}>Live connection</div>
              <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>
                {connectionEntity?.status || connectionEntity?.live_status || "unknown"}
              </div>
              <div style={{ fontSize: 13, opacity: 0.85, marginTop: 12 }}>Range</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>
                {data.range.from} to {data.range.to}
              </div>
            </div>
          </div>
        </section>

        <section style={{ ...cardStyle, marginTop: 18 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "end" }}>
            <div>
              <label style={{ display: "block", fontSize: 13, color: "#475569", marginBottom: 6 }}>Rep</label>
              <select
                value={data.entity.repSlug}
                onChange={(e) => handleEntityChange(e.target.value)}
                style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #cbd5e1", minWidth: 260 }}
              >
                {selectableEntities.map((entity) => (
                  <option key={entity.id} value={entity.normalized_rep_slug}>
                    {entity.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 13, color: "#475569", marginBottom: 6 }}>From</label>
              <input
                type="date"
                value={draftFrom}
                onChange={(e) => setDraftFrom(e.target.value)}
                style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #cbd5e1", minWidth: 160 }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 13, color: "#475569", marginBottom: 6 }}>To</label>
              <input
                type="date"
                value={draftTo}
                onChange={(e) => setDraftTo(e.target.value)}
                style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #cbd5e1", minWidth: 160 }}
              />
            </div>
            <button
              onClick={handleApplyRange}
              style={{
                padding: "10px 16px",
                borderRadius: 10,
                border: "none",
                background: "#14213d",
                color: "#fff",
                fontWeight: 700,
                cursor: "pointer",
                minHeight: 42,
              }}
            >
              Apply range
            </button>
          </div>
        </section>

        <section
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 16,
            marginTop: 18,
          }}
        >
          {[
            ["Included chats", data.summary.includedChats],
            ["Direct chats", data.summary.directChats],
            ["Lead group chats", data.summary.groupChats],
            ["Avg appointment score", data.summary.avgAppointmentScore],
            ["Avg rapport score", data.summary.avgRapportScore],
            ["Avg selling score", data.summary.avgSellingScore],
            ["Strong appointment signals", data.summary.chatsWithStrongAppointmentSignals],
            ["Chats with no outbound", data.summary.chatsWithNoOutboundMessages],
          ].map(([label, value]) => (
            <div key={String(label)} style={cardStyle}>
              <div style={{ color: "#64748b", fontSize: 14 }}>{label}</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: "#14213d", marginTop: 10 }}>{value}</div>
            </div>
          ))}
        </section>

        <section style={{ ...cardStyle, marginTop: 18 }}>
          <h2 style={{ margin: 0, fontSize: 22, color: "#14213d" }}>Overall conclusion</h2>
          <p style={{ margin: "14px 0 0", color: "#334155", lineHeight: 1.7 }}>
            This review is based on the WhatsApp entity itself, not CRM owner mapping. In this period,
            {` ${data.entity.name}`} had {data.summary.includedChats} client-facing chats, with average scores of{" "}
            {data.summary.avgAppointmentScore}/10 for appointment setting, {data.summary.avgRapportScore}/10 for rapport,
            and {data.summary.avgSellingScore}/10 for selling. {data.summary.chatsWithNoOutboundMessages > 0
              ? ` ${data.summary.chatsWithNoOutboundMessages} chats showed no outbound reply in the selected window, which is the biggest immediate risk.`
              : " No chats were left completely unanswered in the selected window."}
          </p>
        </section>

        <section style={{ marginTop: 18 }}>
          <h2 style={{ margin: "0 0 14px", fontSize: 22, color: "#14213d" }}>Chat-by-chat analysis</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 16 }}>
            {data.chats.map((chat) => (
              <article key={chat.chatId} style={cardStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", color: "#8c5b00" }}>
                      {chat.chatType}
                    </div>
                    <h3 style={{ margin: "8px 0 0", fontSize: 22, color: "#14213d", lineHeight: 1.2 }}>{chat.contact}</h3>
                    <div style={{ marginTop: 8, color: "#64748b", fontSize: 13 }}>
                      Last activity: {formatDateTime(chat.lastActivity)}
                    </div>
                  </div>
                  <div
                    style={{
                      alignSelf: "flex-start",
                      padding: "8px 12px",
                      borderRadius: 999,
                      background: chat.includedInReview ? "#eef6ff" : "#eef2f7",
                      color: chat.includedInReview ? "#1d4ed8" : "#475569",
                      fontWeight: 800,
                      fontSize: 13,
                    }}
                  >
                    {chat.includedInReview ? chat.verdict : "Excluded"}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
                  <div>
                    <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>Appointment</div>
                    <div style={scorePill(chat.appointmentScore)}>{chat.appointmentScore}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>Rapport</div>
                    <div style={scorePill(chat.rapportScore)}>{chat.rapportScore}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>Selling</div>
                    <div style={scorePill(chat.sellingScore)}>{chat.sellingScore}</div>
                  </div>
                </div>

                <p style={{ margin: "16px 0 0", color: "#334155", lineHeight: 1.7 }}>{chat.summary}</p>

                <div style={{ marginTop: 14, color: "#475569", fontSize: 13 }}>
                  {chat.metrics.totalMessages} messages • {chat.metrics.outboundMessages} outbound • {chat.metrics.inboundMessages} inbound
                  {chat.metrics.responseMinutes !== null ? ` • first response ~${Math.round(chat.metrics.responseMinutes)} min` : ""}
                </div>

                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#166534", textTransform: "uppercase" }}>What went well</div>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18, lineHeight: 1.65, color: "#334155" }}>
                    {chat.whatWentWell.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>

                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#b91c1c", textTransform: "uppercase" }}>What went wrong</div>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18, lineHeight: 1.65, color: "#334155" }}>
                    {chat.whatWentWrong.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>

                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#8c5b00", textTransform: "uppercase" }}>Improve next time</div>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18, lineHeight: 1.65, color: "#334155" }}>
                    {chat.improvements.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>

                <div style={{ marginTop: 16, background: "#f8fafc", borderRadius: 14, padding: 14, border: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#334155", textTransform: "uppercase" }}>Evidence</div>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18, lineHeight: 1.65, color: "#475569" }}>
                    {chat.evidence.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </section>

        {includedChats.length === 0 && (
          <section style={{ ...cardStyle, marginTop: 18 }}>
            No client-facing chats were found in this date range.
          </section>
        )}
      </div>
    </div>
  );
};

export default SalesReviewDashboard;
